import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import * as notificationAPI from "../api/notifications";
import { fetchUnreadBadge } from "../store/slices/messengerSlice";
import { hasAdminAccess } from "../utils/roleAccess";
import { useAgencyScope, useExtraAgents } from "../components/common/AgencyScopeSwitch";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  // Drive the sidebar badge from the messenger Redux slice so it clears the
  // moment a thread is opened (fetchThread.fulfilled zeros the per-session
  // unread_count + drops the badge). The local interval below keeps the slice
  // warm for users sitting on non-messenger pages. `unreadBadge` (not
  // `unreadTotal`) is the main-inbox count: unreadTotal follows whichever list
  // the Messenger page is showing, so it reads 0 while an agent browses
  // Archived and the badge would blink out under them.
  const messengerUnread = useSelector((state) => state.messenger?.unreadBadge || 0);
  // The Off / On Market Pipeline badges have to describe the board the user is
  // actually shown, and whether that board is personal or team-wide is a
  // browser-side choice - so read the same stored switch the page reads.
  const { agencyWide: pipelineAgencyWide } = useAgencyScope("pipeline");
  // Buyer Enquiries & Offers has its own switch (its own surface key), so its
  // badge has to carry that one, not the boards'. Since 3 Sep 2026 an admin's
  // enquiries page defaults to their own desk, and a badge counting the office
  // over a page showing one agent is the exact bug we already fixed once.
  const {
    agencyWide: enquiriesAgencyWide,
    canSwitch: canScopeEnquiries,
    canIncludeAgents: canIncludeEnquiryAgents,
  } = useAgencyScope("enquiries");
  // The "+ Include an agent" picker is the admin tier, one step wider than the
  // office switch, so the badge follows THAT flag or it would stop counting a
  // colleague the page is showing.
  const { agentIds: enquiriesExtraAgents } = useExtraAgents("enquiries", canIncludeEnquiryAgents);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastCheck, setLastCheck] = useState(new Date());
  // Sidebar badge numbers. Each one is the count of rows the page it sits on
  // would actually show this user - the backend derives them from that page's
  // own queryset (Christian Gravias, Monday 12944444309: a 99+ badge over an
  // empty page). Nothing here is a running total of anything.
  const [pipelineCounts, setPipelineCounts] = useState({
    buyer_enquiries: 0,
    off_market_pipeline: 0,
    on_market_pipeline: 0,
    new_seller_leads: 0,
  });

  const refreshPipelineCounts = useCallback(async () => {
    try {
      const data = await notificationAPI.getPipelineCounts({
        agencyWide: pipelineAgencyWide,
        enquiriesAgencyWide: canScopeEnquiries ? enquiriesAgencyWide : undefined,
        enquiriesAgents: enquiriesExtraAgents,
      });
      setPipelineCounts({
        buyer_enquiries: data?.buyer_enquiries || 0,
        // off_market_offers / on_market_offers are the old key names, kept as a
        // fallback so a stale cached bundle and a fresh API agree either way.
        off_market_pipeline: data?.off_market_pipeline ?? data?.off_market_offers ?? 0,
        on_market_pipeline: data?.on_market_pipeline ?? data?.on_market_offers ?? 0,
        // The Prospects row: seller leads nobody has called yet (Monday 12988442847).
        new_seller_leads: data?.new_seller_leads || 0,
      });
    } catch (err) {
      // silent — sidebar badges are non-critical
    }
  }, [pipelineAgencyWide, canScopeEnquiries, enquiriesAgencyWide, enquiriesExtraAgents]);

  const refreshMessengerUnread = useCallback(() => {
    dispatch(fetchUnreadBadge());
  }, [dispatch]);

  // A badge has to fall the moment the agent clears the row behind it, not up
  // to 60s later. invalidateCache.leads()/.properties() fire these events from
  // every ignore / assign / restore / delete on the pages the badges point at
  // (Christian Gravias, Monday 12944444309).
  useEffect(() => {
    if (!user) return;
    const onChange = () => refreshPipelineCounts();
    window.addEventListener('cache-invalidated:leads', onChange);
    window.addEventListener('cache-invalidated:properties', onChange);
    return () => {
      window.removeEventListener('cache-invalidated:leads', onChange);
      window.removeEventListener('cache-invalidated:properties', onChange);
    };
  }, [user, refreshPipelineCounts]);

  // Load initial notifications
  useEffect(() => {
    if (user) {
      loadNotifications();
      refreshPipelineCounts();
      refreshMessengerUnread();
      const pipelineId = setInterval(refreshPipelineCounts, 60000);
      const messengerId = setInterval(refreshMessengerUnread, 30000);
      return () => {
        clearInterval(pipelineId);
        clearInterval(messengerId);
      };
    }
  }, [user, refreshPipelineCounts, refreshMessengerUnread]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      // For development, use mock data
      const mockNotifications = notificationAPI.getMockNotifications();
      setNotifications(mockNotifications);
      setUnreadCount(mockNotifications.filter((n) => !n.read).length);
    } catch (error) {
      console.error("Error loading notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  // const startRealTimeUpdates = () => {
  //   if (!user) return;

  //   const interval = setInterval(() => {
  //     if (Math.random() < 0.1) {
  //       // addRandomNotification();
  //     }
  //   }, 30000);

  //   return () => clearInterval(interval);
  // };

  const addRandomNotification = () => {
    const notificationTypes = [
      "hot_lead_assigned",
      "warm_lead_assigned",
      "callback_reminder",
      "handoff_request",
      "escalation",
      "ai_call_completed",
      "system_alert",
    ];

    const randomType =
      notificationTypes[Math.floor(Math.random() * notificationTypes.length)];
    const names = [
      "Emma Wilson",
      "Michael Chen",
      "Sarah Johnson",
      "David Brown",
      "Lisa Anderson",
    ];
    const randomName = names[Math.floor(Math.random() * names.length)];

    const newNotification = {
      id: Date.now(),
      type: randomType,
      title: `${getNotificationTypeLabel(randomType)} - ${randomName}`,
      message: `New ${getNotificationTypeLabel(
        randomType
      ).toLowerCase()} notification for ${randomName}.`,
      timestamp: new Date().toISOString(),
      read: false,
      priority: getNotificationPriority(randomType),
      data: {
        contactName: randomName,
        leadId: `L${Date.now()}`,
        phone: `+61 4${Math.floor(Math.random() * 9)}${Math.floor(
          Math.random() * 9
        )} ${Math.floor(Math.random() * 9)}${Math.floor(
          Math.random() * 9
        )}${Math.floor(Math.random() * 9)} ${Math.floor(
          Math.random() * 9
        )}${Math.floor(Math.random() * 9)}${Math.floor(Math.random() * 9)}`,
      },
    };

    setNotifications((prev) => [newNotification, ...prev]);
    setUnreadCount((prev) => prev + 1);

    // Show toast notification
    toast.success(`New ${getNotificationTypeLabel(randomType).toLowerCase()}`);
  };

  const getNotificationTypeLabel = (type) => {
    const labels = {
      hot_lead_assigned: "Hot Lead Assigned",
      warm_lead_assigned: "Warm Lead Assigned",
      callback_reminder: "Callback Reminder",
      handoff_request: "Live Handoff Request",
      escalation: "Lead Escalation",
      ai_call_completed: "AI Call Completed",
      system_alert: "System Alert",
    };
    return labels[type] || "Notification";
  };

  const getNotificationPriority = (type) => {
    const priorities = {
      hot_lead_assigned: "high",
      warm_lead_assigned: "high",
      callback_reminder: "medium",
      handoff_request: "high",
      escalation: "high",
      ai_call_completed: "medium",
      system_alert: "high",
    };
    return priorities[type] || "medium";
  };

  const markAsRead = async (notificationId) => {
    try {
      // Update local state immediately
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      // Call API
      await notificationAPI.markNotificationAsRead(notificationId);
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      // Update local state immediately
      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, read: true }))
      );
      setUnreadCount(0);

      // Call API
      await notificationAPI.markAllNotificationsAsRead();
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      toast.error("Failed to mark all notifications as read");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      // Update local state immediately
      const notification = notifications.find((n) => n.id === notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

      if (notification && !notification.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      // Call API
      await notificationAPI.deleteNotification(notificationId);
      toast.success("Notification deleted");
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error("Failed to delete notification");
    }
  };

  const deleteMultipleNotifications = async (notificationIds) => {
    try {
      // Update local state immediately
      const deletedNotifications = notifications.filter((n) =>
        notificationIds.includes(n.id)
      );
      const unreadDeleted = deletedNotifications.filter((n) => !n.read).length;

      setNotifications((prev) =>
        prev.filter((n) => !notificationIds.includes(n.id))
      );
      setUnreadCount((prev) => Math.max(0, prev - unreadDeleted));

      // Call API
      await notificationAPI.deleteMultipleNotifications(notificationIds);
      toast.success(`${notificationIds.length} notifications deleted`);
    } catch (error) {
      console.error("Error deleting multiple notifications:", error);
      toast.error("Failed to delete notifications");
    }
  };

  const handleNotificationAction = async (notification) => {
    try {
      // Mark as read first
      await markAsRead(notification.id);

      // Handle specific actions
      switch (notification.type) {
        case "hot_lead_assigned":
        case "warm_lead_assigned":
          await notificationAPI.handleLeadNotification(
            notification.id,
            notification.data.leadId
          );
          window.location.href = `/leads?leadId=${notification.data.leadId}`;
          break;

        case "callback_reminder":
          await notificationAPI.handleCallbackNotification(
            notification.id,
            notification.data
          );
          window.location.href = `/calendar?callback=${notification.data.contactName}`;
          break;

        case "handoff_request":
          await notificationAPI.handleHandoffNotification(
            notification.id,
            notification.data.callId
          );
          window.location.href = `/ai-calls?handoff=${notification.data.callId}`;
          break;

        case "escalation":
          await notificationAPI.handleEscalationNotification(
            notification.id,
            notification.data
          );
          if (hasAdminAccess(user)) {
            window.location.href = `/admin-properties?escalation=${notification.data.leadId}`;
          } else {
            window.location.href = `/leads?escalation=${notification.data.leadId}`;
          }
          break;

        default:
          // Just mark as read for other types
          break;
      }
    } catch (error) {
      console.error("Error handling notification action:", error);
      toast.error("Failed to process notification action");
    }
  };

  const getNotificationStats = async () => {
    try {
      const stats = await notificationAPI.getNotificationStats();
      return stats;
    } catch (error) {
      console.error("Error fetching notification stats:", error);
      return {
        total: notifications.length,
        unread: unreadCount,
        highPriority: notifications.filter((n) => n.priority === "high").length,
        today: notifications.filter((n) => {
          const today = new Date().toDateString();
          return new Date(n.timestamp).toDateString() === today;
        }).length,
      };
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    buyerEnquiryCount: pipelineCounts.buyer_enquiries,
    offMarketPipelineCount: pipelineCounts.off_market_pipeline,
    onMarketPipelineCount: pipelineCounts.on_market_pipeline,
    prospectsNewCount: pipelineCounts.new_seller_leads,
    refreshPipelineCounts,
    messengerUnread,
    refreshMessengerUnread,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteMultipleNotifications,
    handleNotificationAction,
    getNotificationStats,
    loadNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
